import { FEATURES } from '@electron/shared';
import {
  getAuthModule,
  isAuthSessionError,
  shouldForceLogout,
} from '@littlebird/auth-client';
import { createSDK, getSDK, hasSDK } from '@littlebird/sdk';

import { getText } from '@/components/ui/editor/utils';
import { getEnvVar } from '@/env';
import { chatEngineStore } from '@/features/chat/chat-engine.store';
import { screenContextSearchStore } from '@/features/chat/screen-context-search.store';
import { electronApis } from '@/features/electron-api';
import { hasFeature } from '@/features/feature-flags';
import { inElectron } from '@/utils';

import { appStore } from '../stores/app.store';
import { userStore } from '../stores/user.store';

import { IPCWebSocketAdapter } from './ipc-websocket-adapter';
import { createAdapters } from './local-db/create-adapters';
import { createSyncAxiomTransport } from './local-db/sync-axiom-transport';
import { sdkLogger } from './logger';

const windowLabel = window.systemInfo?.currentWindow?.label;
const skipSearchIndex =
  windowLabel === 'hummingbird' || windowLabel === 'meeting-status';
// If we are in Electron: on a dev hot-reload re-run, reuse the adapter the
// existing SDK already owns; otherwise create the first one. Reuse saves us
// from a nasty bug: with two live adapters, both feed the same SDK stores,
// so every chat message chunk would come through twice.
//
// In prod, none of this matters: this file runs once when there's no SDK yet,
// so all we do is create the first and only adapter.
const ipcAdapter = inElectron()
  ? hasSDK()
    ? // Safe cast: the Electron build only ever gives its SDK an IPCWebSocketAdapter
      (getSDK().wsManager as IPCWebSocketAdapter)
    : new IPCWebSocketAdapter()
  : undefined;
// Read before createSDK() below sets the singleton, so a dev hot-reload re-run sees hasSDK() === true
const isFirstElectronLoad = inElectron() && !hasSDK();
const meetingNotesToText = (notes: Parameters<typeof getText>[0]) =>
  getText(notes);
let rendererSessionDeviceId: string | null = null;

const { storageAdapter, searchAdapter, notifyCrossWindowSync } = createAdapters(
  {
    skipSearchIndex,
    meetingNotesToText,
    getSdk: () => (hasSDK() ? getSDK() : undefined),
  }
);

/**
 * Return the ID the backend uses to send WebSocket response audio only to this
 * Electron app. The web app returns null because it has no Electron connection
 * ID; this does not disable Voice Mode. The backend broadcasts audio to the
 * user's connections, and the frontend accepts it only when its chat and
 * user-message IDs match the active Voice Mode session.
 */
async function getSdkDeviceId(): Promise<string | null> {
  if (!inElectron()) return null;
  if (rendererSessionDeviceId) return rendererSessionDeviceId;

  try {
    rendererSessionDeviceId = await electronApis.system.getDeviceId();
  } catch {
    // Missing identity broadens WebSocket delivery to a broadcast; it must not
    // prevent the chat request or WebSocket connection
    return null;
  }
  return rendererSessionDeviceId;
}

export const littlebirdSdk = createSDK({
  createStorageAdapter: () => storageAdapter,
  createSearchAdapter: () => searchAdapter,
  platform: inElectron() ? 'desktop' : 'web',
  onNetworkIssue: () => appStore.reportConnectionProblem(),
  apiBaseUrl: getEnvVar('VITE_API_URL'),
  wsBaseUrl: getEnvVar('VITE_WS_URL'),
  capabilities: {
    addCompressionHeader: true,
  },
  getAccessToken: () => getAuthModule().getAccessToken(),
  refreshAccessToken: async ({ rejectedToken, detail }) => {
    try {
      await getAuthModule().tokenManager.tokenAfterUnauthorized(
        rejectedToken,
        detail
      );
      return 'refreshed';
    } catch (error) {
      // Only the reasons the auth client signs out for end the session
      if (isAuthSessionError(error) && shouldForceLogout(error.reason)) {
        return 'session_ended';
      }
      throw error;
    }
  },
  getVersion: () => Promise.resolve(__APP_VERSION__),
  getDeviceId: getSdkDeviceId,
  getScreenContextSearchDisabled: (threadId) =>
    screenContextSearchStore.disabledByChat[threadId],
  apiV2: {
    baseUrl: getEnvVar('VITE_API_V2_URL'),
    getChatEngine: (threadId) => chatEngineStore.engineByChat[threadId],
  },
  getUserHeader: () =>
    Promise.resolve({
      ...userStore.userHeader,
      'x-request-source': `renderer:${window.systemInfo?.currentWindow?.label ?? 'unknown'}`,
    }),
  logger: sdkLogger,
  webSocketManager: ipcAdapter,
  meetingNotesToText,
  isSyncFingerprintSkipEnabled: () =>
    !hasFeature(FEATURES.DISABLE_SYNC_FINGERPRINT_SKIP),
  isChatTurnOwnershipEnabled: () =>
    !hasFeature(FEATURES.DISABLE_STALE_DONE_EVENT_GUARD),
});

export { notifyCrossWindowSync };

// Hand the adapter its SDK reference and register its IPC handlers. In
// Electron, `ipcAdapter` is the same object `littlebirdSdk` holds as
// `wsManager`. In the web app, `ipcAdapter` is `undefined` and the SDK's
// built-in WebSocket manager needs no wiring here. Repeating this call on a
// dev hot-reload re-run is safe: `setSDK` first disposes whatever handlers
// the adapter already registered.
ipcAdapter?.setSDK(littlebirdSdk);

// Lives here rather than in logger/index.ts: electron-api imports the logger, so the logger cannot import electron-api
if (isFirstElectronLoad) {
  sdkLogger.addTransport(
    createSyncAxiomTransport({
      send: (event) => electronApis.syncTelemetry.logSyncEvent(event),
      getSyncRequestId: () =>
        hasSDK() ? getSDK().currentSyncRequestId : undefined,
    })
  );
}
